"use client"

import { useState, useRef, useEffect, useLayoutEffect, useCallback, useId, type ReactNode } from "react"
import { createPortal } from "react-dom"

interface MenuProps {
  /** Contenido del botón que abre el menú. */
  trigger: ReactNode
  /** Contenido del panel. Recibe `close` para cerrarse tras una acción. */
  children: (close: () => void) => ReactNode
  /** Alineación del panel respecto al trigger. */
  align?: "start" | "end"
  triggerClassName?: string
  panelClassName?: string
  "aria-label": string
}

// Margen mínimo contra los bordes del viewport y separación trigger→panel.
const EDGE = 8
const GAP = 8

// Spec: docs/design-system/2026-07-30-MENU_PORTAL_CLAMP_SPEC.md
export function Menu({ trigger, children, align = "end", triggerClassName = "", panelClassName = "", ...rest }: MenuProps) {
  const [open, setOpen] = useState(false)
  // Los tokens --pos-* se definen por [data-pos-theme]; al portalear el panel a
  // <body> sale del subárbol temado, así que el atributo se copia al panel o
  // los var(--pos-*) de panelClassName dejarían de resolver.
  const [posTheme, setPosTheme] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const id = useId()
  const close = () => setOpen(false)

  // Posicionamiento imperativo sobre el DOM (sin estado): el efecto solo
  // sincroniza el panel con el layout, y scroll/resize no re-renderizan React.
  const place = useCallback(() => {
    const root = rootRef.current
    const panel = panelRef.current
    if (!root || !panel) return
    const anchor = root.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    // Quitar maxHeight para medir hace que el contenido "quepa" un instante y el
    // navegador clampe scrollTop a 0 — se preserva y restaura al final, o cada
    // reposicionamiento (resize, scroll externo) resetearía el scroll del panel.
    const prevScrollTop = panel.scrollTop
    panel.style.maxHeight = "" // medir la altura natural para decidir el flip
    const pw = panel.offsetWidth
    const ph = panel.offsetHeight

    let left = align === "end" ? anchor.right - pw : anchor.left
    left = Math.max(EDGE, Math.min(left, vw - pw - EDGE))

    // Debajo del trigger por defecto; flip arriba solo si no cabe y arriba hay
    // más espacio. maxHeight acota al espacio elegido y el panel scrollea.
    const below = vh - anchor.bottom - GAP - EDGE
    const above = anchor.top - GAP - EDGE
    const flip = ph > below && above > below
    const maxHeight = Math.max(120, flip ? above : below)
    const top = flip
      ? Math.max(EDGE, anchor.top - GAP - Math.min(ph, maxHeight))
      : anchor.bottom + GAP

    panel.style.top = `${top}px`
    panel.style.left = `${left}px`
    panel.style.maxHeight = `${maxHeight}px`
    panel.scrollTop = prevScrollTop
    panel.style.visibility = "visible"
  }, [align])

  useLayoutEffect(() => {
    if (open) place()
  }, [open, place])

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false) }
    // Los scroll no burbujean pero SÍ descienden en fase de captura: el scroll
    // INTERNO del panel también aterriza aquí. Reposicionar en ese caso pisa el
    // scroll del usuario y las opciones del fondo de un menú desbordado quedan
    // inalcanzables — solo interesa el scroll de fuera (mueve el trigger).
    function onScroll(e: Event) {
      if (e.target instanceof Node && panelRef.current?.contains(e.target)) return
      place()
    }
    document.addEventListener("mousedown", onDocClick)
    document.addEventListener("keydown", onKey)
    window.addEventListener("resize", place)
    document.addEventListener("scroll", onScroll, { capture: true, passive: true })
    return () => {
      document.removeEventListener("mousedown", onDocClick)
      document.removeEventListener("keydown", onKey)
      window.removeEventListener("resize", place)
      document.removeEventListener("scroll", onScroll, { capture: true })
    }
  }, [open, place])

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        aria-label={rest["aria-label"]}
        onClick={() => {
          setPosTheme(rootRef.current?.closest("[data-pos-theme]")?.getAttribute("data-pos-theme") ?? null)
          setOpen((v) => !v)
        }}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open && createPortal(
        <div
          id={id}
          ref={panelRef}
          role="menu"
          data-pos-theme={posTheme ?? undefined}
          style={{ visibility: "hidden" }} // hidden hasta que place() lo posiciona pre-paint
          className={[
            "fixed z-[90] min-w-[220px] max-w-[calc(100vw-16px)] overflow-y-auto overscroll-contain rounded-[14px] p-1.5",
            "bg-[var(--redy-surface)] border border-[var(--redy-border)] shadow-xl",
            panelClassName,
          ].filter(Boolean).join(" ")}
        >
          {children(close)}
        </div>,
        document.body
      )}
    </div>
  )
}
